/**
 * CharacterSpatialIndex - O(log n) spatial indexing for character lookup
 * 
 * Uses simplified spatial indexing for fast character box queries
 * Handles complex text layouts: multi-line, RTL, ligatures, clusters
 * Optimized for EdgeDocs legal document editing performance
 */

import { 
  CharacterBox, 
  CharacterMetadata, 
  CanvasCoordinate
} from '../interfaces/CaretInterfaces';
import { CoordinateSpace } from './UnifiedCoordinateSystem';

// Simplified spatial index using grid-based approach
interface SpatialGrid {
  cellSize: number;
  grid: Map<string, CharacterBox[]>;
  bounds: { minX: number; maxX: number; minY: number; maxY: number };
}

export class CharacterSpatialIndex {
  private spatialGrid: SpatialGrid;
  private characterMap: Map<number, CharacterBox> = new Map();
  private lineMap: Map<number, CharacterBox[]> = new Map();
  private isBuilt: boolean = false;
  
  // Performance tracking
  private buildTime: number = 0;
  private queryCount: number = 0;
  private totalQueryTime: number = 0;
  
  constructor(cellSize: number = 50) {
    this.spatialGrid = {
      cellSize,
      grid: new Map(),
      bounds: { minX: 0, maxX: 0, minY: 0, maxY: 0 }
    };
  }
  
  // ============================================================================
  // INDEX BUILDING
  // ============================================================================
  
  /**
   * Build spatial index from backend layout plan
   */
  buildFromLayoutPlan(layoutPlan: any): void {
    const startTime = performance.now();
    console.log('[CharacterSpatialIndex] Building spatial index...');
    
    // Clear existing index
    this.clear();
    
    if (!layoutPlan?.pages?.[0]?.frames?.[0]?.lines) {
      console.warn('[CharacterSpatialIndex] No layout data available');
      return;
    }
    
    let globalCharIndex = 0;
    const allCharacterBoxes: CharacterBox[] = [];
    
    // Extract all character boxes from layout plan
    for (let lineIndex = 0; lineIndex < layoutPlan.pages[0].frames[0].lines.length; lineIndex++) {
      const line = layoutPlan.pages[0].frames[0].lines[lineIndex];
      const lineCharacters: CharacterBox[] = [];
      
      for (let runIndex = 0; runIndex < (line.runs || []).length; runIndex++) {
        const run = line.runs[runIndex];
        
        if (!run?.charBoxes || !run?.text) {
          globalCharIndex += run?.text?.length || 0;
          continue;
        }
        
        for (let charInRun = 0; charInRun < run.charBoxes.length; charInRun++) {
          const charBox = run.charBoxes[charInRun];
          const [x, y, width, height] = charBox;
          
          const characterBox: CharacterBox = {
            index: globalCharIndex,
            bounds: { x, y, width, height },
            lineIndex,
            runIndex,
            charInRun,
            metadata: this.analyzeCharacterMetadata(
              run.text[charInRun], 
              globalCharIndex, 
              run,
              line
            )
          };
          
          // Add to all collections
          allCharacterBoxes.push(characterBox);
          this.characterMap.set(globalCharIndex, characterBox);
          lineCharacters.push(characterBox);
          
          // Update spatial bounds
          this.updateBounds(x, y, width, height);
          
          globalCharIndex++;
        }
      }
      
      this.lineMap.set(lineIndex, lineCharacters);
    }
    
    // Build spatial grid
    this.buildSpatialGrid(allCharacterBoxes);
    
    this.buildTime = performance.now() - startTime;
    this.isBuilt = true;
    
    console.log(`[CharacterSpatialIndex] Index built in ${this.buildTime.toFixed(2)}ms:`, {
      characters: globalCharIndex,
      lines: this.lineMap.size,
      gridCells: this.spatialGrid.grid.size,
      bounds: this.spatialGrid.bounds
    });
  }
  
  /**
   * Build spatial grid for O(log n) lookups
   */
  private buildSpatialGrid(characterBoxes: CharacterBox[]): void {
    this.spatialGrid.grid.clear();
    
    for (const charBox of characterBoxes) {
      const cellKey = this.getCellKey(charBox.bounds.x, charBox.bounds.y);
      
      if (!this.spatialGrid.grid.has(cellKey)) {
        this.spatialGrid.grid.set(cellKey, []);
      }
      
      this.spatialGrid.grid.get(cellKey)!.push(charBox);
    }
  }
  
  private getCellKey(x: number, y: number): string {
    const cellX = Math.floor(x / this.spatialGrid.cellSize);
    const cellY = Math.floor(y / this.spatialGrid.cellSize);
    return `${cellX},${cellY}`;
  }
  
  private updateBounds(x: number, y: number, width: number, height: number): void {
    const bounds = this.spatialGrid.bounds;
    
    bounds.minX = Math.min(bounds.minX, x);
    bounds.maxX = Math.max(bounds.maxX, x + width);
    bounds.minY = Math.min(bounds.minY, y);
    bounds.maxY = Math.max(bounds.maxY, y + height);
  }
  
  // ============================================================================
  // HIT TESTING
  // ============================================================================
  
  /**
   * O(log n) hit testing using spatial grid
   */
  hitTest(canvasX: number, canvasY: number): CharacterBox | null {
    if (!this.isBuilt) {
      console.warn('[CharacterSpatialIndex] Index not built, cannot perform hit test');
      return null;
    }
    
    const startTime = performance.now();
    this.queryCount++;
    
    // Get candidate cells (current cell + surrounding cells for tolerance)
    const candidates = this.getCandidateCharacters(canvasX, canvasY);
    
    // Find exact hit
    for (const candidate of candidates) {
      if (this.pointInBounds(canvasX, canvasY, candidate.bounds)) {
        const queryTime = performance.now() - startTime;
        this.totalQueryTime += queryTime;
        
        console.log(`✅ [CharacterSpatialIndex] Exact hit found in ${queryTime.toFixed(3)}ms:`, {
          characterIndex: candidate.index,
          bounds: candidate.bounds,
          candidatesChecked: candidates.length
        });
        
        return candidate;
      }
    }
    
    // Find nearest character if no exact hit
    const nearest = this.findNearestCharacter(canvasX, canvasY, candidates);
    
    const queryTime = performance.now() - startTime;
    this.totalQueryTime += queryTime;
    
    if (nearest) {
      console.log(`⚠️ [CharacterSpatialIndex] Nearest hit found in ${queryTime.toFixed(3)}ms:`, {
        characterIndex: nearest.character.index,
        distance: nearest.distance.toFixed(2),
        candidatesChecked: candidates.length
      });
      
      return nearest.character;
    }
    
    console.warn(`❌ [CharacterSpatialIndex] No hit found in ${queryTime.toFixed(3)}ms`);
    return null;
  }
  
  /**
   * Get character position by index (for caret rendering)
   */
  getCharacterPosition(index: number): CanvasCoordinate | null {
    const characterBox = this.characterMap.get(index);
    if (!characterBox) return null;
    
    return {
      x: characterBox.bounds.x,
      y: characterBox.bounds.y + characterBox.bounds.height * 0.8, // Baseline position
      space: CoordinateSpace.CANVAS,
      precision: 'subpixel',
      timestamp: performance.now()
    };
  }
  
  /**
   * Get all characters in a range (for selection highlighting)
   */
  getCharactersInRange(startIndex: number, endIndex: number): CharacterBox[] {
    const characters: CharacterBox[] = [];
    
    for (let i = startIndex; i <= endIndex; i++) {
      const charBox = this.characterMap.get(i);
      if (charBox) {
        characters.push(charBox);
      }
    }
    
    return characters;
  }
  
  // ============================================================================
  // PRIVATE UTILITIES
  // ============================================================================
  
  private getCandidateCharacters(x: number, y: number): CharacterBox[] {
    const candidates: CharacterBox[] = [];
    
    // Check current cell and surrounding cells for tolerance
    const cellKeys = [
      this.getCellKey(x, y),
      this.getCellKey(x - this.spatialGrid.cellSize, y),
      this.getCellKey(x + this.spatialGrid.cellSize, y),
      this.getCellKey(x, y - this.spatialGrid.cellSize),
      this.getCellKey(x, y + this.spatialGrid.cellSize)
    ];
    
    for (const cellKey of cellKeys) {
      const cellCharacters = this.spatialGrid.grid.get(cellKey);
      if (cellCharacters) {
        candidates.push(...cellCharacters);
      }
    }
    
    return candidates;
  }
  
  private pointInBounds(x: number, y: number, bounds: { x: number; y: number; width: number; height: number }): boolean {
    return x >= bounds.x && 
           x < bounds.x + bounds.width &&
           y >= bounds.y && 
           y < bounds.y + bounds.height;
  }
  
  private findNearestCharacter(
    x: number, 
    y: number, 
    candidates: CharacterBox[]
  ): { character: CharacterBox; distance: number } | null {
    let nearest: CharacterBox | null = null;
    let minDistance = Infinity;
    
    for (const candidate of candidates) {
      const centerX = candidate.bounds.x + candidate.bounds.width / 2;
      const centerY = candidate.bounds.y + candidate.bounds.height / 2;
      const distance = Math.sqrt((x - centerX) ** 2 + (y - centerY) ** 2);
      
      if (distance < minDistance) {
        minDistance = distance;
        nearest = candidate;
      }
    }
    
    return nearest ? { character: nearest, distance: minDistance } : null;
  }
  
  private analyzeCharacterMetadata(
    char: string, 
    index: number, 
    run: any,
    line: any
  ): CharacterMetadata {
    return {
      character: char,
      unicodeCategory: this.getUnicodeCategory(char),
      isClusterStart: true, // Simplified - would use Unicode analysis
      isLigature: false,    // Would be detected from HarfBuzz data
      
      // Text structure (simplified analysis)
      isWordStart: /\s/.test(char) || index === 0,
      isWordEnd: false,     // Would be computed with lookahead
      isParagraphStart: char === '\n' || index === 0,
      isParagraphEnd: char === '\n',
      isLineStart: run.charInRun === 0,
      isLineEnd: false,     // Would be computed with run analysis
      
      // Typography
      fontFamily: 'Times New Roman', // From layout plan
      fontSize: 12,                  // From layout plan
      isRTL: run.dir === 'rtl',
      
      // Legal document properties (would be enhanced)
      isInCitation: false,
      isInClause: false
    };
  }
  
  private getUnicodeCategory(char: string): string {
    if (/[a-zA-Z]/.test(char)) return 'Letter';
    if (/[0-9]/.test(char)) return 'Number';
    if (/\s/.test(char)) return 'Whitespace';
    if (/[.,;:!?]/.test(char)) return 'Punctuation';
    return 'Other';
  }
  
  // ============================================================================
  // UTILITIES
  // ============================================================================
  
  /**
   * Clear the spatial index
   */
  clear(): void {
    this.spatialGrid.grid.clear();
    this.characterMap.clear();
    this.lineMap.clear();
    this.isBuilt = false;
    
    // Reset bounds
    this.spatialGrid.bounds = { minX: 0, maxX: 0, minY: 0, maxY: 0 };
  }
  
  /**
   * Get performance statistics
   */
  getPerformanceStats(): {
    buildTime: number;
    averageQueryTime: number;
    totalQueries: number;
    indexSize: number;
  } {
    return {
      buildTime: this.buildTime,
      averageQueryTime: this.queryCount > 0 ? this.totalQueryTime / this.queryCount : 0,
      totalQueries: this.queryCount,
      indexSize: this.characterMap.size
    };
  }
  
  /**
   * Validate index integrity
   */
  validateIndex(): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    if (!this.isBuilt) {
      errors.push('Index not built');
    }
    
    if (this.characterMap.size === 0) {
      errors.push('No characters in index');
    }
    
    // Check for gaps in character indices
    const indices = Array.from(this.characterMap.keys()).sort((a, b) => a - b);
    for (let i = 1; i < indices.length; i++) {
      if (indices[i] !== indices[i-1] + 1) {
        errors.push(`Gap in character indices: ${indices[i-1]} -> ${indices[i]}`);
      }
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }
  
  /**
   * Get debug information
   */
  getDebugInfo(): any {
    return {
      isBuilt: this.isBuilt,
      characterCount: this.characterMap.size,
      lineCount: this.lineMap.size,
      gridCellCount: this.spatialGrid.grid.size,
      bounds: this.spatialGrid.bounds,
      performance: this.getPerformanceStats(),
      validation: this.validateIndex()
    };
  }
}
